<!-- Prevent prettier to remove parentheses from v-model type cast -->
<!-- prettier-ignore -->
<template>
  <div class="pb-80">
    <!-- Product infos -->
    <div class="flex gap-x-56">
      <div class="flex w-full grow flex-col">
        <RevBreadcrumb :ariaLabel="i18n(translations.breadcrumb)" :breads />
        <div class="flex grow flex-col justify-center">
          <h1 class="heading-1">{{ currentMarketQuote?.product.title }}</h1>
          <p v-if="currentMarketQuote?.product.productId" class="body-1 mt-16">
            {{
              i18n(translations.productId, { productId: currentMarketQuote?.product.productId })
            }}
          </p>
          <div class="mt-32">
            <h2 class="heading-2">{{ i18n(translations.marketTitle) }}</h2>
            <div
              v-if="activeMarket"
              class="mt-16 grid grid-cols-2 gap-12 md:grid-cols-3"
            >
              <RevRadio
                v-for="market in currentMarketQuote?.product.availableMarkets"
                :id="market"
                :key="market"
                v-model="activeMarket"
                name="market"
                :value="market"
                variant="full"
              >
                <template #label>
                  <div class="flex gap-4">
                    <Flag :country-code="(market as MarketCountryCode)" />
                    {{ market }}
                  </div>
                </template>
              </RevRadio>
            </div>
          </div>
        </div>
      </div>
      <RevIllustration
        v-if="currentMarketQuote?.product.image"
        alt=""
        class="hidden lg:block"
        data-test="quote-product-illustration"
        :height="440"
        :src="currentMarketQuote?.product.image"
        :width="440"
      />
    </div>
    <div class="mt-32 grow">
      <div class="flex">
        <h2 class="heading-2 grow">
          {{ i18n(translations.priceTitle) }}
        </h2>
        <RevButton
          class="md:hidden"
          :disabled="isWinAllBackboxesDisabled"
          size="small"
          variant="primary"
          @click="handleGetAllBackBoxes"
        >
          {{ i18n(translations.getAllBackboxes) }}
        </RevButton>
      </div>
      <div
        class="border-static-default-low relative z-20 hidden min-h-56 items-center border-b [&>p]:body-1-bold md:grid"
        :class="gridClasses"
      >
        <p class="pl-8">{{ i18n(translations.grade) }}</p>
        <div class="body-1-bold flex items-center">
          <p>
            {{ i18n(translations.priceToWinBackboxTitle) }}
          </p>
          <RevTooltip position="bottom-end">
            <template #trigger="{ show, hide }">
              <RevButtonIcon
                class="cursor-pointer"
                :disabled="false"
                :icon="IconInfo"
                variant="secondary"
                @focusin="show"
                @focusout="hide"
                @mouseleave="hide"
                @mouseover="show"
              />
            </template>
            {{ i18n(translations.priceToWinBackboxTooltip) }}
          </RevTooltip>
        </div>
        <p>{{ i18n(translations.backboxPrice) }}</p>
        <p>{{ i18n(translations.yourPrice) }}</p>
        <p>{{ i18n(translations.sku) }}</p>
        <div class="flex justify-end">
          <RevTooltip position="top-end">
            <template #trigger="{ show, hide }">
              <RevButton
                class="hidden justify-self-end md:block"
                :disabled="isWinAllBackboxesDisabled"
                size="small"
                variant="primary"
                @click="handleGetAllBackBoxes"
                @focusin="() => handleTooltipVisibility(show, hide)"
                @focusout="hide"
                @mouseleave="hide"
                @mouseover="() => handleTooltipVisibility(show, hide)"
              >
                {{ i18n(translations.getAllBackboxes) }}
              </RevButton>
            </template>
            {{ i18n(translations.alreadyHaveAllBackbox)}}
          </RevTooltip>
        </div>
      </div>
      <QuoteListing
        v-for="(listing) in currentMarketQuote?.listings"
        :key="listing.grade"
        v-model:price="listing.price"
        v-model:sku="listing.sku"
        :grid-classes
        :is-loading="isLoading && loadingListings.includes(listing.grade)"
        :is-price-to-win-loading="isPriceToWinLoading && listing.state === LISTING_PUBLICATION_STATE.OFFLINE"
        :listing
        :market-code="activeMarket"
        :pricing-estimation="getPricingEstimationByGrade(pricingEstimations, listing.grade)"
        @get-backbox-clicked="handleGetBackbox(listing)"
      >
      </QuoteListing>
    </div>
    <RevStickyBar class="z-30">
      <div class="py-12 md:px-32">
        <div class="hidden md:flex">
          <RevButton
            class="mr-12"
            :disabled="isSavingListings"
            :loading="isSavingListingsWithRedirect"
            variant="secondary"
            @click="saveListings({ redirect: true })"
          >
            {{ i18n(translations.saveListingCreate) }}
          </RevButton>
          <RevButton
            :disabled="isSavingListingsWithRedirect"
            :loading="isSavingListings"
            variant="primary"
            @click="saveListings({ redirect: false })"
          >
            {{ i18n(translations.saveListing) }}
          </RevButton>
        </div>
        <div class="flex md:hidden">
          <RevButton
            class="mr-2"
            :disabled="isSavingListings"
            :loading="isSavingListingsWithRedirect"
            size="small"
            variant="secondary"
            @click="saveListings({ redirect: true })"
          >
            {{ i18n(translations.saveListingCreateMobile) }}
          </RevButton>
          <RevButton
            :disabled="isSavingListingsWithRedirect"
            :loading="isSavingListings"
            size="small"
            variant="primary"
            @click="saveListings({ redirect: false })"
          >
            {{ i18n(translations.saveListing) }}
          </RevButton>
        </div>
      </div>
    </RevStickyBar>
    <QuotesTimeoutToast
        v-model="isTimeoutToastOpen"
    ></QuotesTimeoutToast>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed, onMounted, ref, toRaw, watch } from 'vue'

import type { HttpApiError } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { getBuybackQuote } from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-quotes'
import type {
  BuyBackQuote,
  BuyBackQuoteListing,
  BuyBackQuotePricingEstimation,
  PostBuyBackQuoteListing,
} from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-quotes.types'
import { LISTING_PUBLICATION_STATE } from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.constants'
import toastTranslations from '@backmarket/nuxt-layer-buyback/utils/Toast.translations'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { getMarketplace } from '@backmarket/nuxt-module-marketplace/getMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { sleep } from '@backmarket/utils/async/sleep'
import { cloneDeep } from '@backmarket/utils/object/cloneDeep'
import { tw } from '@backmarket/utils/string/tw'
import { RevBreadcrumb } from '@ds/components/Breadcrumb'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevIllustration } from '@ds/components/Illustration'
import { RevRadio } from '@ds/components/Radio'
import { RevStickyBar } from '@ds/components/StickyBar'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconInfo } from '@ds/icons/IconInfo'

import { ROUTE_NAMES } from '~/constants/routes'
import QuotesTimeoutToast from '~/scopes/buyback/components/QuotesTimeoutToast/QuotesTimeoutToast.vue'
import { useGetPricingEstimations } from '~/scopes/buyback/composables/useGetPricingEstimation'
import {
  BUYBACK_QUOTES_FETCH_INTERVAL,
  BUYBACK_QUOTES_FETCH_TIMEOUT,
} from '~/scopes/buyback/constants'
import {
  getPricingEstimationByGrade,
  matchPostAndGetListings,
  removeDecimalsFromString,
} from '~/scopes/buyback/utils/buyback.utils'

import { useUpdateListings } from '../wishlist/composables/useUpdateListings'

import translations from './CreateProductQuotations.translations'
import QuoteListing from './components/QuoteListing.vue'

const route = useRoute()
const i18n = useI18n()
const logger = useLogger()
const { closeToast, openErrorToast, openSuccessToast } = useTheToast()

const currentMarketQuote = ref<BuyBackQuote | null>(null)
const canonicalListings = ref<BuyBackQuoteListing[] | null>(null)
const pricingEstimations = ref<BuyBackQuotePricingEstimation[]>([])

const activeMarket = ref<string>('')
const loadingListings = ref<string[]>([])
const isQuoteLoading = ref(false)
const isPriceToWinLoading = ref(true)
const isSavingListings = ref(false)
const isSavingListingsWithRedirect = ref(false)
const gridClasses = tw`gap-x-16 md:grid-cols-[minmax(auto,148px)_1fr_1fr_1fr_1fr_minmax(196px,auto)] lg:grid-cols-6`
const isSaving = computed(
  () => isSavingListings.value || isSavingListingsWithRedirect.value,
)

if (typeof route.query.countryCode === 'string') {
  activeMarket.value = route.query.countryCode
}

const pathParams = {
  productId: route.params.productId,
}

const requestParams =
  activeMarket.value === ''
    ? { pathParams }
    : { pathParams, queryParams: { marketCode: activeMarket.value } }

const { error: quoteError, data: quoteData } = await useHttpFetch(
  getBuybackQuote,
  requestParams,
)

if (quoteError.value) {
  openErrorToast({
    title: i18n(toastTranslations.toastErrorTitle),
    content: i18n(toastTranslations.toastErrorContent),
  })

  logger.error('[BUYBACK][API][QUOTES] Error getting quote', {
    error: quoteError.value,
    owners: ['bot-squad-circularity-order-lifecycle-front'],
  })
}

function cloneListings(listings: BuyBackQuoteListing[]): BuyBackQuoteListing[] {
  return listings.map((listing) => cloneDeep(toRaw(listing)))
}

if (quoteData.value) {
  currentMarketQuote.value = quoteData.value
  canonicalListings.value = cloneListings(quoteData.value.listings)
}

async function getQuote(marketCode: string): Promise<BuyBackQuote | null> {
  try {
    isQuoteLoading.value = true

    const buybackQuote = await $httpFetch(getBuybackQuote, {
      pathParams,
      queryParams: { marketCode },
    })

    isQuoteLoading.value = false

    return buybackQuote
  } catch (err) {
    openErrorToast({
      title: i18n(toastTranslations.toastErrorTitle),
      content: i18n(toastTranslations.toastErrorContent),
    })

    logger.error('[BUYBACK][API][QUOTES] Error getting quote', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })

    isQuoteLoading.value = false

    return null
  }
}

async function updatePricingEstimations() {
  if (currentMarketQuote.value === null) {
    return
  }

  // Empty pricing estimations on each call to retrigger recomputation on subcomponents
  pricingEstimations.value = []

  const offlineListings = currentMarketQuote.value.listings.filter(
    (listing) => listing.state === LISTING_PUBLICATION_STATE.OFFLINE,
  )

  if (offlineListings.length === 0) {
    isPriceToWinLoading.value = false

    return
  }

  isPriceToWinLoading.value = true

  const { product, marketCode } = currentMarketQuote.value
  const offlineGrades = offlineListings.map((listing) => listing.grade)

  pricingEstimations.value = await useGetPricingEstimations(
    offlineGrades,
    product.productId,
    marketCode,
  )

  isPriceToWinLoading.value = false
}

if (activeMarket.value === '' && currentMarketQuote.value) {
  activeMarket.value = currentMarketQuote.value.marketCode
}

async function handleActiveMarketUpdate(countryCode: string) {
  activeMarket.value = countryCode

  if (route.name) {
    navigateTo({
      name: route.name,
      params: route.params,
      query: { countryCode },
    })

    const updatedQuote = await getQuote(activeMarket.value)

    currentMarketQuote.value = updatedQuote
    canonicalListings.value = updatedQuote
      ? cloneListings(updatedQuote.listings)
      : null

    await updatePricingEstimations()
  }
}

watch(activeMarket, (countryCode: string) => {
  handleActiveMarketUpdate(countryCode)
})

const breads = computed(() => [
  {
    title: i18n(translations.createListingTitle),
    link: {
      name: ROUTE_NAMES.BUYBACK.SEARCH_PRODUCT,
    },
  },
  { title: currentMarketQuote.value?.product.title || '', slug: '', link: '' },
])

const currency = computed(() =>
  activeMarket.value
    ? getMarketplace(activeMarket.value as MarketCountryCode)?.market
        .defaultCurrency
    : null,
)

const isLoading = computed(() => isQuoteLoading.value || isSaving.value)

const hasAllBackboxes = computed(() => {
  if (!currentMarketQuote.value) {
    return false
  }

  const listingsWithoutBackbox = currentMarketQuote.value.listings.filter(
    (listing) => {
      const isPriceAboveBackboxPrice =
        Number(listing.price?.amount) < Number(listing.backboxPrice?.amount)

      return listing.hasBackbox === false || isPriceAboveBackboxPrice
    },
  )

  return listingsWithoutBackbox.length === 0
})

/**
 * We don't want to enable the button if there is no competition (no backbox price on any listing),
 * or if the refurbisher already have all the backboxes
 */
const isWinAllBackboxesDisabled = computed(() => {
  if (!currentMarketQuote.value) {
    return false
  }

  const hasNoBackboxPrices = currentMarketQuote.value.listings.every(
    (listing) => !listing.backboxPrice,
  )

  return hasNoBackboxPrices || hasAllBackboxes.value
})

function handleTooltipVisibility(show: () => Promise<void>, hide: () => void) {
  return hasAllBackboxes.value ? show() : hide()
}

const isTimeoutToastOpen = ref(false)
let startTime: number | null = null
async function pollUpdatedListings(
  listingsPostPayload: PostBuyBackQuoteListing[],
) {
  if (!startTime) {
    startTime = Date.now()
  }

  const upToDateQuote = await getQuote(activeMarket.value)
  if (upToDateQuote === null) {
    return
  }

  if (Date.now() - startTime > BUYBACK_QUOTES_FETCH_TIMEOUT) {
    closeToast()
    startTime = null
    isTimeoutToastOpen.value = true
    loadingListings.value = []

    currentMarketQuote.value = upToDateQuote
    canonicalListings.value = cloneListings(upToDateQuote.listings)

    return
  }

  if (!matchPostAndGetListings(listingsPostPayload, upToDateQuote.listings)) {
    loadingListings.value = listingsPostPayload.map((l) => l.grade)
    await sleep(BUYBACK_QUOTES_FETCH_INTERVAL)
    await pollUpdatedListings(listingsPostPayload)

    return
  }

  startTime = null
  loadingListings.value = []
  currentMarketQuote.value = upToDateQuote
  canonicalListings.value = cloneListings(upToDateQuote.listings)
  openSuccessToast({
    title: i18n(translations.toastTitle),
  })
}

function handleGetBackbox(listing: BuyBackQuoteListing) {
  const index = currentMarketQuote.value?.listings.findIndex(
    (l) => l.grade === listing.grade,
  )

  const pricingEstimation = getPricingEstimationByGrade(
    pricingEstimations.value,
    listing.grade,
  )

  const priceToWinBackbox =
    listing.priceToWinBackbox || pricingEstimation?.priceToWinBackbox

  if (
    !currency.value ||
    !currentMarketQuote.value ||
    index === undefined ||
    index < 0 ||
    !priceToWinBackbox
  ) {
    return
  }

  const price = {
    amount: removeDecimalsFromString(priceToWinBackbox.amount),
    currency: currency.value,
  }

  currentMarketQuote.value.listings[index] = {
    ...listing,
    price,
  }
}

function handleGetAllBackBoxes() {
  currentMarketQuote.value?.listings.forEach((listing) =>
    handleGetBackbox(listing),
  )
}

function adaptListingsPostPayload(
  listings: BuyBackQuoteListing[],
): PostBuyBackQuoteListing[] {
  return listings
    .map((listing) => {
      const listingPayload: PostBuyBackQuoteListing = {
        grade: listing.grade,
      }

      if (!canonicalListings.value) {
        return listingPayload
      }

      const canonicalListing = canonicalListings.value.find(
        (l) => l.grade === listing.grade,
      )

      if (!canonicalListing) {
        return listingPayload
      }

      const priceString = listing.price
        ? removeDecimalsFromString(listing.price.amount)
        : null
      if (
        listing.price?.amount !== canonicalListing.price?.amount &&
        priceString
      ) {
        listingPayload.price = {
          amount: priceString,
          currency: listing.price.currency,
        }
      }

      if (listing.sku !== canonicalListing.sku) {
        listingPayload.sku = listing.sku
      }

      return listingPayload
    })
    .filter(
      (listing) =>
        listing &&
        (Object.hasOwn(listing, 'price') || Object.hasOwn(listing, 'sku')),
    )
}

async function saveListings({ redirect }: { redirect: boolean }) {
  if (!currentMarketQuote.value) {
    return
  }

  const listingsPayload = adaptListingsPostPayload(
    currentMarketQuote.value.listings,
  )

  if (!listingsPayload.length) {
    openSuccessToast({
      title: i18n(translations.toastTitle),
    })

    return
  }

  isTimeoutToastOpen.value = false
  isSavingListings.value = !redirect
  isSavingListingsWithRedirect.value = redirect

  const { shouldFetch } = await useUpdateListings({
    productId: String(route.params.productId),
    marketCode: activeMarket.value,
    listings: listingsPayload,
  })

  if (shouldFetch) {
    await pollUpdatedListings(listingsPayload)
    await updatePricingEstimations()
  }

  if (redirect && isTimeoutToastOpen.value === false) {
    navigateTo({
      name: ROUTE_NAMES.BUYBACK.SEARCH_PRODUCT,
    })

    return
  }

  isSavingListings.value = false
  isSavingListingsWithRedirect.value = false
}

onMounted(async () => {
  await updatePricingEstimations()
})
</script>
